import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, MessageCircle } from 'lucide-react';
import type { CredentialResponse } from '@react-oauth/google';
import { useRyanAccess } from '../hooks/useRyanAccess';
import { useAuth } from '../../contexts/AuthContext';
import GoogleLoginButton from '../../components/GoogleLoginButton';
import './RyanLoginPage.css';

// Mesmo número padrão usado como fallback em RyanHomePage.tsx/RyanFieldPage.tsx
// (DEFAULT_CONFIG) — aqui não dá pra buscar o RyanConfig configurado pelo admin
// porque essa rota exige acesso Ryan (justamente o que a pessoa não tem nesta tela).
const SUPPORT_WHATSAPP_NUMBER = '5577988362587';

function buildSupportWhatsappUrl(): string {
  const cleaned = SUPPORT_WHATSAPP_NUMBER.replace(/\D/g, '');
  const full = cleaned.startsWith('55') ? cleaned : `55${cleaned}`;
  const msg = 'Oi! Tentei entrar na minha conta da GRAIA Medicina mas não consegui acessar. Pode me ajudar?';
  return `https://wa.me/${full}?text=${encodeURIComponent(msg)}`;
}

/* ── Ilustrações decorativas do fundo — traço branco translúcido sobre o
   gradiente verde, motivos ligados à medicina, cada uma com viewBox própria
   pra manter as proporções ao ser posicionada via CSS (RyanLoginPage.css). ── */
const IconStethoscope: React.FC = () => (
  <svg viewBox="0 0 100 120" fill="none">
    <g stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 6 V26 C22 40 32 48 44 48 C56 48 66 40 66 26 V14" />
      <circle cx="22" cy="6" r="4" />
      <circle cx="66" cy="14" r="4" />
      <path d="M44 48 V70 C44 82 54 90 54 90" />
      <circle cx="60" cy="96" r="12" />
      <circle cx="60" cy="96" r="4.5" />
    </g>
  </svg>
);

const IconAsclepius: React.FC = () => (
  <svg viewBox="0 0 100 130" fill="none">
    <g stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <line x1="50" y1="10" x2="50" y2="122" strokeWidth={3.2} />
      {/* corpo da cobra — traço bem mais grosso que o bastão pra ler como
          cobra enrolada à primeira vista, não como uma linha decorativa */}
      <path d="M50 24 C68 32 69 48 50 56 C31 64 30 80 50 88 C68 95 69 110 50 118" strokeWidth={5.5} fill="none" />
      {/* hachuras de escama ao longo do corpo, reforçando a leitura de cobra */}
      <path d="M58 30 L64 26 M61 44 L68 42 M58 52 L64 56 M42 64 L36 60 M39 78 L32 79 M42 86 L36 90 M58 94 L65 91 M61 108 L68 111" strokeWidth={2} opacity="0.85" />
      {/* cabeça — maior e mais definida, com focinho e mandíbula. Olhos com
          opacidade cheia sobre a cabeça em 0.7 pra ler mais claros que o
          resto do traço, mesmo dentro da baixa opacidade geral do ícone. */}
      <path d="M50 6 L60 20 L55 31 L50 34 L45 31 L40 20 Z" fill="currentColor" opacity="0.7" stroke="none" />
      <circle cx="45.5" cy="19" r="1.8" fill="currentColor" stroke="none" />
      <circle cx="54.5" cy="19" r="1.8" fill="currentColor" stroke="none" />
      {/* língua bífida */}
      <path d="M50 6 l-3 -6 M50 6 l3 -6 M50 6 l0 -4" strokeWidth={1.6} opacity="0.7" />
    </g>
  </svg>
);

const IconHospital: React.FC = () => (
  <svg viewBox="0 0 100 100" fill="none">
    <g stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
      <rect x="12" y="34" width="76" height="58" rx="2" />
      <path d="M50 44 v20 M40 54 h20" strokeWidth={3.2} />
      <line x1="22" y1="66" x2="30" y2="66" />
      <line x1="70" y1="66" x2="78" y2="66" />
      <line x1="22" y1="78" x2="30" y2="78" />
      <line x1="70" y1="78" x2="78" y2="78" />
      <rect x="44" y="76" width="12" height="16" />
    </g>
  </svg>
);

const IconHeartSus: React.FC = () => (
  <svg viewBox="0 0 140 110" fill="none">
    <g stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
      <path d="M70 34 C 70 18 90 14 96 28 C 102 40 90 52 70 66 C 50 52 38 40 44 28 C 50 14 70 18 70 34 Z" />
      <path d="M70 30 v18 M61 39 h18" strokeWidth={3} />
    </g>
    <text x="70" y="94" textAnchor="middle" fontSize="13" fontWeight={700} fill="currentColor" fontFamily="Inter, sans-serif" letterSpacing="0.5">
      #VivaoSUS
    </text>
  </svg>
);

/* Seringa reta — barril, agulha e êmbolo alinhados na mesma linha de centro
   (y=20), sem o efeito "torto" da versão anterior (agulha angulada saindo de
   um barril horizontal). */
const IconSyringe: React.FC = () => (
  <svg viewBox="0 0 150 40" fill="none">
    <g stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="11" x2="8" y2="29" strokeWidth={3.2} />
      <line x1="8" y1="20" x2="28" y2="20" strokeWidth={2.8} />
      <line x1="28" y1="13" x2="28" y2="27" strokeWidth={2.4} />
      <rect x="30" y="11" width="52" height="18" rx="2" strokeWidth={2.8} />
      <line x1="42" y1="14" x2="42" y2="26" strokeWidth={1.6} />
      <line x1="54" y1="14" x2="54" y2="26" strokeWidth={1.6} />
      <line x1="66" y1="14" x2="66" y2="26" strokeWidth={1.6} />
      <line x1="78" y1="14" x2="78" y2="26" strokeWidth={1.6} />
      <path d="M82 12 L96 17 L96 23 L82 28" strokeWidth={2.4} />
      <line x1="96" y1="20" x2="140" y2="20" strokeWidth={2.2} />
      <circle cx="144" cy="20" r="2" fill="currentColor" stroke="none" />
    </g>
  </svg>
);

const IconPillPack: React.FC = () => (
  <svg viewBox="0 0 100 60" fill="none">
    <g stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="6" width="88" height="48" rx="6" />
      <circle cx="24" cy="20" r="7" />
      <circle cx="50" cy="20" r="7" />
      <circle cx="76" cy="20" r="7" />
      <circle cx="24" cy="40" r="7" />
      <circle cx="50" cy="40" r="7" />
      <circle cx="76" cy="40" r="7" />
    </g>
  </svg>
);

const IconAmbulance: React.FC = () => (
  <svg viewBox="0 0 140 80" fill="none">
    <g stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 54 V30 h64 v-8 h28 l16 18 v14" />
      <rect x="6" y="30" width="64" height="24" />
      <path d="M70 30 h28 l16 18 h-44 Z" />
      <circle cx="30" cy="60" r="8" />
      <circle cx="98" cy="60" r="8" />
      <line x1="38" y1="60" x2="90" y2="60" />
      <path d="M28 36 v10 M23 41 h10" strokeWidth={2.4} />
    </g>
  </svg>
);

const IconCross: React.FC = () => (
  <svg viewBox="0 0 40 40" fill="none">
    <path d="M20 6 v28 M6 20 h28" stroke="currentColor" strokeWidth={5} strokeLinecap="round" />
  </svg>
);

/* Traçado de ECG reconhecível: onda P (bolha), complexo QRS (pico agudo) e
   onda T (bolha), em vez de um zigue-zague genérico. */
const IconPulseLine: React.FC = () => (
  <svg viewBox="0 0 140 40" fill="none">
    <path
      d="M0 20 H18 Q22 12 26 20 H34 L38 27 L41 3 L45 31 L49 20 H58 Q64 12 70 20 H140"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RyanLoginBackground: React.FC = () => (
  <div className="ryan-login-bg" aria-hidden="true">
    <div className="ryan-login-bg-icon ryan-login-bg-icon--stethoscope"><IconStethoscope /></div>
    <div className="ryan-login-bg-icon ryan-login-bg-icon--asclepius"><IconAsclepius /></div>
    <div className="ryan-login-bg-icon ryan-login-bg-icon--hospital"><IconHospital /></div>
    <div className="ryan-login-bg-icon ryan-login-bg-icon--heart-sus"><IconHeartSus /></div>
    <div className="ryan-login-bg-icon ryan-login-bg-icon--syringe"><IconSyringe /></div>
    <div className="ryan-login-bg-icon ryan-login-bg-icon--pillpack"><IconPillPack /></div>
    <div className="ryan-login-bg-icon ryan-login-bg-icon--ambulance"><IconAmbulance /></div>
    <div className="ryan-login-bg-icon ryan-login-bg-icon--cross-a"><IconCross /></div>
    <div className="ryan-login-bg-icon ryan-login-bg-icon--cross-b"><IconCross /></div>
    <div className="ryan-login-bg-icon ryan-login-bg-icon--pulse"><IconPulseLine /></div>
    <div className="ryan-login-bg-icon ryan-login-bg-icon--stethoscope-sm"><IconStethoscope /></div>
  </div>
);

type Mode = 'login' | 'forgot' | 'signup';

const RyanLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { checkRyanAccess } = useRyanAccess();
  const { checkAuth } = useAuth();

  // ?mode=signup permite deep-link direto pra aba "Criar conta" (ex: badge de
  // presente "Gratuito" na página pública do produto) sem exigir dois cliques.
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<Mode>(searchParams.get('mode') === 'signup' ? 'signup' : 'login');

  /* Login */
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // true quando o login em si funcionou (email/senha corretos) mas a conta não
  // tem acesso à GRAIA Medicina — mostrado como card à parte, mais acolhedor
  // que o erro simples de credenciais inválidas, com saída pelo WhatsApp.
  const [noAccess, setNoAccess] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleSignupLoading, setGoogleSignupLoading] = useState(false);
  const [googleSignupNoAccess, setGoogleSignupNoAccess] = useState(false);

  /* Forgot password */
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSubmitted, setForgotSubmitted] = useState(false);

  /* Criar conta — sem produto envolvido, ver ensureEmptyRyanAccess no backend */
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupWhatsapp, setSignupWhatsapp] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [signupAcceptedTerms, setSignupAcceptedTerms] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNoAccess(false);
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, platform: 'ryan' }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Email ou senha incorretos.');
        return;
      }

      // Credenciais corretas não significam acesso à GRAIA Medicina — é a mesma
      // conta usada no GRAIA ENEM. Verifica antes de navegar, em vez de deixar
      // o RyanProtectedRoute barrar em /ryan com uma tela totalmente diferente
      // (é isso que fazia parecer um redirecionamento pra "outra página").
      const meRes = await fetch('/api/ryan/me', {
        headers: { Authorization: `Bearer ${data.accessToken}` },
      });
      const meData = await meRes.json().catch(() => ({ hasAccess: false }));
      if (!meRes.ok || !meData.hasAccess) {
        setNoAccess(true);
        return;
      }

      localStorage.setItem('accessToken', data.accessToken);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
      await Promise.all([checkAuth(), checkRyanAccess()]);
      navigate('/ryan');
    } catch {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Chamada de rede compartilhada entre "Entrar com Google" e "Criar conta
  // com Google" — os dois usam a MESMA rota (POST /api/auth/google, platform
  // 'ryan') e a mesma checagem pós-auth de acesso; só o estado de UI que
  // reporta o resultado (login vs. signup) muda entre os dois handlers abaixo.
  type GoogleAuthData = { accessToken: string; refreshToken?: string; userId: string; username: string; email: string; role: string };
  type GoogleAuthResult =
    | { ok: true; data: GoogleAuthData }
    | { ok: false; noAccess: true }
    | { ok: false; noAccess: false; message: string };

  const authenticateWithGoogle = async (credential?: string): Promise<GoogleAuthResult> => {
    const res = await fetch('/api/auth/google', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential, platform: 'ryan' }),
    });
    const data = await res.json();
    if (!res.ok) return { ok: false, noAccess: false, message: data.message || 'Erro ao continuar com Google.' };

    // Conta nova criada aqui já recebe um RyanAccess ativo vazio no backend
    // (ver POST /api/auth/google, platform 'ryan'), então não fica presa na
    // tela de "sem acesso" logo em seguida — mas uma conta Google já
    // existente sem acesso à GRAIA Medicina ainda precisa passar por aqui.
    const meRes = await fetch('/api/ryan/me', {
      headers: { Authorization: `Bearer ${data.accessToken}` },
    });
    const meData = await meRes.json().catch(() => ({ hasAccess: false }));
    if (!meRes.ok || !meData.hasAccess) return { ok: false, noAccess: true };

    return { ok: true, data };
  };

  const finishGoogleAuth = async (data: GoogleAuthData) => {
    localStorage.setItem('accessToken', data.accessToken);
    if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify({
      userId: data.userId, username: data.username, email: data.email, role: data.role,
    }));
    await Promise.all([checkAuth(), checkRyanAccess()]);
    navigate('/ryan');
  };

  // Entrar com Google
  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    setError('');
    setNoAccess(false);
    setGoogleLoading(true);
    try {
      const result = await authenticateWithGoogle(credentialResponse.credential);
      if (!result.ok) {
        if (result.noAccess) setNoAccess(true);
        else setError(result.message);
        return;
      }
      await finishGoogleAuth(result.data);
    } catch {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setGoogleLoading(false);
    }
  };

  // Criar conta com Google — mesma rota/handler de "Entrar com Google" (uma
  // conta Google nova é criada automaticamente do lado do backend); só
  // reporta erro/"sem acesso" no estado da aba de signup em vez da de login.
  const handleGoogleSignup = async (credentialResponse: CredentialResponse) => {
    setSignupError('');
    setGoogleSignupNoAccess(false);
    setGoogleSignupLoading(true);
    try {
      const result = await authenticateWithGoogle(credentialResponse.credential);
      if (!result.ok) {
        if (result.noAccess) setGoogleSignupNoAccess(true);
        else setSignupError(result.message);
        return;
      }
      await finishGoogleAuth(result.data);
    } catch {
      setSignupError('Erro de conexão. Tente novamente.');
    } finally {
      setGoogleSignupLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);
    try {
      await fetch('/api/ryan/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      setForgotSubmitted(true);
    } catch {
      setForgotError('Erro de conexão. Tente novamente.');
    } finally {
      setForgotLoading(false);
    }
  };

  const goToForgot = () => {
    setMode('forgot');
    setForgotEmail('');
    setForgotError('');
    setForgotSubmitted(false);
  };

  const goToSignup = () => {
    setMode('signup');
    setSignupName('');
    setSignupEmail('');
    setSignupWhatsapp('');
    setSignupPassword('');
    setSignupConfirmPassword('');
    setSignupAcceptedTerms(false);
    setSignupError('');
    setGoogleSignupNoAccess(false);
  };

  const goToLogin = () => {
    setMode('login');
    setError('');
    setNoAccess(false);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignupError('');
    if (signupPassword.length < 6) { setSignupError('A senha deve ter no mínimo 6 caracteres.'); return; }
    if (signupPassword !== signupConfirmPassword) { setSignupError('As senhas não coincidem.'); return; }
    if (!signupAcceptedTerms) { setSignupError('É necessário concordar com os Termos de Uso.'); return; }

    setSignupLoading(true);
    try {
      const res = await fetch('/api/ryan/auth/criar-conta', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: signupName, email: signupEmail, whatsapp: signupWhatsapp,
          password: signupPassword, confirmPassword: signupConfirmPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setSignupError(data.message || 'Erro ao criar conta.');
        return;
      }

      localStorage.setItem('accessToken', data.accessToken);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify({
        userId: data.userId, username: data.username, email: data.email, role: data.role,
      }));
      await Promise.all([checkAuth(), checkRyanAccess()]);
      navigate('/ryan');
    } catch {
      setSignupError('Erro de conexão. Tente novamente.');
    } finally {
      setSignupLoading(false);
    }
  };

  return (
    <div className="ryan-login-root">
      <RyanLoginBackground />
      <div className="ryan-login-card">
        <img src="/Ryan/graia-logo-verde.png" alt="Graia Medicina" className="ryan-login-logo" />
        <h1 className="ryan-login-title">Graia Medicina</h1>

        {mode === 'login' ? (
          <>
            <form className="ryan-login-form" onSubmit={handleLogin}>
              {error && <div className="ryan-login-error">{error}</div>}

              <div className="ryan-login-field">
                <label className="ryan-login-label">Email</label>
                <input
                  type="email"
                  className="ryan-login-input"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  autoFocus
                />
              </div>

              <div className="ryan-login-field">
                <label className="ryan-login-label">Senha</label>
                <div className="ryan-login-pw-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="ryan-login-input"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    className="ryan-login-pw-toggle"
                    onClick={() => setShowPassword(s => !s)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button type="submit" className="ryan-login-btn" disabled={loading}>
                {loading ? 'Entrando…' : 'Entrar'}
              </button>
            </form>

            <div className="ryan-login-divider"><span>ou</span></div>

            <div className="ryan-login-google-wrap">
              <GoogleLoginButton
                onSuccess={handleGoogleSuccess}
                onError={() => setError('Erro ao entrar com Google.')}
              />
              {googleLoading && <p className="ryan-login-subtitle">Entrando…</p>}
            </div>

            <div className="ryan-login-links-row">
              <button className="ryan-login-forgot-btn" onClick={goToForgot}>
                Esqueceu a senha?
              </button>
              <button className="ryan-login-forgot-btn" onClick={goToSignup}>
                Criar conta
              </button>
            </div>

            {noAccess && (
              <div className="ryan-login-noaccess-card">
                <p className="ryan-login-noaccess-text">
                  Não encontramos uma conta com acesso à GRAIA Medicina com esses dados.
                  Se você já comprou algum material, fale com a gente que resolvemos rapidinho.
                </p>
                <a
                  className="ryan-login-noaccess-btn"
                  href={buildSupportWhatsappUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MessageCircle size={15} /> Falar no WhatsApp
                </a>
              </div>
            )}
          </>
        ) : mode === 'signup' ? (
          <>
            <p className="ryan-login-subtitle">Criar conta</p>

            <form className="ryan-login-form" onSubmit={handleSignup}>
              {signupError && <div className="ryan-login-error">{signupError}</div>}

              <div className="ryan-login-field">
                <label className="ryan-login-label">Nome</label>
                <input
                  type="text"
                  className="ryan-login-input"
                  value={signupName}
                  onChange={e => setSignupName(e.target.value)}
                  placeholder="Seu nome completo"
                  required
                  autoFocus
                />
              </div>

              <div className="ryan-login-field">
                <label className="ryan-login-label">Email</label>
                <input
                  type="email"
                  className="ryan-login-input"
                  value={signupEmail}
                  onChange={e => setSignupEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                />
              </div>

              <div className="ryan-login-field">
                <label className="ryan-login-label">WhatsApp</label>
                <input
                  type="text"
                  className="ryan-login-input"
                  value={signupWhatsapp}
                  onChange={e => setSignupWhatsapp(e.target.value)}
                  placeholder="(11) 91234-5678"
                  required
                />
              </div>

              <div className="ryan-login-field">
                <label className="ryan-login-label">Senha</label>
                <input
                  type="password"
                  className="ryan-login-input"
                  value={signupPassword}
                  onChange={e => setSignupPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                />
              </div>

              <div className="ryan-login-field">
                <label className="ryan-login-label">Confirmar senha</label>
                <input
                  type="password"
                  className="ryan-login-input"
                  value={signupConfirmPassword}
                  onChange={e => setSignupConfirmPassword(e.target.value)}
                  placeholder="Repita a senha"
                  required
                />
              </div>

              <div className="ryan-login-terms-field">
                <input
                  type="checkbox"
                  id="ryan-signup-terms"
                  className="ryan-login-checkbox"
                  checked={signupAcceptedTerms}
                  onChange={e => setSignupAcceptedTerms(e.target.checked)}
                  disabled={signupLoading}
                />
                <label htmlFor="ryan-signup-terms" className="ryan-login-terms-label">
                  Li e concordo com os{' '}
                  <Link to="/ryan/termos" target="_blank" rel="noopener noreferrer">Termos de Uso</Link>
                  {' '}e a Política de Privacidade
                </label>
              </div>

              <button type="submit" className="ryan-login-btn" disabled={signupLoading || !signupAcceptedTerms}>
                {signupLoading ? 'Criando conta…' : 'Criar conta e entrar'}
              </button>
            </form>

            <div className="ryan-login-divider"><span>ou</span></div>

            <div className={`ryan-login-google-wrap${!signupAcceptedTerms ? ' ryan-login-google-wrap--disabled' : ''}`}>
              <GoogleLoginButton
                onSuccess={handleGoogleSignup}
                onError={() => setSignupError('Erro ao criar conta com Google.')}
              />
              {googleSignupLoading && <p className="ryan-login-subtitle">Criando conta…</p>}
            </div>
            {!signupAcceptedTerms && (
              <p className="ryan-login-google-hint">Marque a caixa acima para criar a conta com o Google</p>
            )}

            {googleSignupNoAccess && (
              <div className="ryan-login-noaccess-card">
                <p className="ryan-login-noaccess-text">
                  Não encontramos uma conta com acesso à GRAIA Medicina com esses dados.
                  Se você já comprou algum material, fale com a gente que resolvemos rapidinho.
                </p>
                <a
                  className="ryan-login-noaccess-btn"
                  href={buildSupportWhatsappUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MessageCircle size={15} /> Falar no WhatsApp
                </a>
              </div>
            )}

            <button className="ryan-login-forgot-btn" onClick={goToLogin}>
              ← Voltar ao login
            </button>
          </>
        ) : (
          <>
            <p className="ryan-login-subtitle">
              {forgotSubmitted ? 'Solicitação enviada' : 'Recuperação de senha'}
            </p>

            {forgotSubmitted ? (
              <div className="ryan-login-forgot-msg">
                <p>
                  Entre em contato com o administrador para receber o link de recuperação de senha.
                </p>
              </div>
            ) : (
              <form className="ryan-login-form" onSubmit={handleForgotPassword}>
                {forgotError && <div className="ryan-login-error">{forgotError}</div>}
                <div className="ryan-login-field">
                  <label className="ryan-login-label">Email da conta</label>
                  <input
                    type="email"
                    className="ryan-login-input"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    placeholder="seu@email.com"
                    required
                    autoFocus
                  />
                </div>
                <button type="submit" className="ryan-login-btn" disabled={forgotLoading}>
                  {forgotLoading ? 'Enviando…' : 'Enviar solicitação'}
                </button>
              </form>
            )}

            <button className="ryan-login-forgot-btn" onClick={goToLogin}>
              ← Voltar ao login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default RyanLoginPage;
